<template>
  <div class="dashboard-home">
    <section class="hero-panel">
      <div class="hero-copy">
        <span class="eyebrow">{{ currentDate }}</span>
        <h1>Welcome back, {{ userName }}</h1>
        <p>
          A quiet command center for jobs, tokens, storage, and the next things
          PostMate is learning to do.
        </p>
        <div class="hero-pulse">
          <span>
            <b-icon icon="activity" />
            {{ dashboardData.jobs.running || 0 }} running
          </span>
          <span>
            <b-icon icon="calendar-check" />
            {{ dashboardData.upcomingPosts || 0 }} upcoming
          </span>
          <span>
            <b-icon icon="shield-check" />
            {{ dashboardData.expiringTokens || 0 }} token alerts
          </span>
        </div>
      </div>

      <div class="hero-side">
        <div class="automation-signal" aria-hidden="true">
          <div class="signal-header">
            <span>Live Queue</span>
            <i></i>
          </div>
          <div class="signal-bars">
            <span v-for="index in 16" :key="index"></span>
          </div>
          <div class="signal-footer">
            <span>SYNC</span>
            <span>{{ dashboardData.jobs.running || 0 }} ACTIVE</span>
          </div>
        </div>

        <div class="hero-actions">
          <b-button to="/new-automation" class="hero-action hero-action-primary">
            <span class="hero-action-icon">
              <b-icon icon="plus-circle" />
            </span>
            <span>New Job</span>
          </b-button>
          <b-button to="/jobs/all" class="hero-action hero-action-secondary">
            <span class="hero-action-icon">
              <b-icon icon="briefcase" />
            </span>
            <span>View Jobs</span>
          </b-button>
        </div>
      </div>
    </section>

    <section v-if="permissions.allowGeneralStats" class="metric-grid">
      <div v-for="metric in metrics" :key="metric.key" class="metric-card">
        <div class="metric-topline">
          <span class="metric-icon" :class="metric.tone">
            <b-icon :icon="metric.icon" />
          </span>
          <span class="metric-label">{{ metric.label }}</span>
        </div>
        <strong>{{ metric.value }}</strong>
        <small>{{ metric.note }}</small>
      </div>
    </section>

    <section class="quick-strip">
      <router-link
        v-for="action in quickActions"
        :key="action.label"
        :to="action.to"
        class="quick-action"
      >
        <span class="quick-action-icon">
          <b-icon :icon="action.icon" />
        </span>
        <span class="quick-action-text">{{ action.label }}</span>
        <b-icon icon="arrow-right-short" class="quick-action-arrow" />
      </router-link>
    </section>

    <section class="dashboard-grid">
      <div class="panel recent-panel">
        <div class="panel-header">
          <div>
            <h2>Recent Automations</h2>
            <p>The latest jobs moving through the queue.</p>
          </div>
          <b-button to="/jobs/all" variant="outline-primary" size="sm">
            All Jobs
          </b-button>
        </div>

        <div v-if="loadingDashboard" class="empty-state">
          <b-spinner small class="mr-2" /> Loading jobs...
        </div>
        <div v-else-if="recentJobs.length === 0" class="empty-state">
          No recent jobs yet.
        </div>
        <div v-else class="job-stream">
          <router-link
            v-for="job in visibleRecentJobs"
            :key="job._id"
            class="job-row"
            :to="'/automation-detail/' + job._id"
          >
            <div class="job-main">
              <span class="job-name">{{ job.name || "Untitled job" }}</span>
              <span class="job-meta">{{ job.pageName || "No page" }}</span>
            </div>
            <span class="job-type">{{ job.postType || "Job" }}</span>
            <span class="status-pill" :class="statusClass(job.status)">
              {{ job.status || "Unknown" }}
            </span>
          </router-link>
        </div>
      </div>

      <div class="panel changelog-panel">
        <div class="panel-header">
          <div>
            <h2>Change Log</h2>
            <p>What changed recently.</p>
          </div>
        </div>

        <div class="change-list">
          <div
            v-for="change in visibleChangeLog"
            :key="change.title"
            class="change-item"
          >
            <span class="change-date">{{ change.date }}</span>
            <div>
              <h3>{{ change.title }}</h3>
              <p>{{ change.body }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- <section class="lower-grid">
      <div v-if="permissions.allowServerStats" class="panel storage-panel">
        <div class="panel-header">
          <div>
            <h2>Storage Health</h2>
            <p>Server storage overview.</p>
          </div>
          <span class="storage-percent">{{ storageData.usedPercentage }}%</span>
        </div>

        <div class="storage-body">
          <div
            class="storage-ring"
            :style="{ '--used': `${storageData.usedPercentage || 0}%` }"
          >
            <span>{{ storageData.usedGB || 0 }}GB</span>
            <small>used</small>
          </div>
          <div class="storage-facts">
            <div>
              <span>Total</span>
              <strong>{{ storageData.totalGB || 0 }} GB</strong>
            </div>
            <div>
              <span>Free</span>
              <strong>{{ storageData.freeGB || 0 }} GB</strong>
            </div>
          </div>
        </div>
      </div>
    </section> -->
  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      loadingDashboard: true,
      loadingStorage: true,
      dashboardData: {
        jobs: {},
        posts: {},
        upcomingPosts: 0,
        expiringTokens: 0,
      },
      recentJobs: [],
      storageData: {
        totalGB: 0,
        freeGB: 0,
        usedGB: 0,
        usedPercentage: 0,
      },
      changeLog: [
        {
          date: "May 9, 2026",
          title: "Append posts to existing jobs",
          body: "Add more photos, reels, or stories to an active job and keep the original schedule cadence.",
        },
        {
          date: "May 9, 2026",
          title: "Token search and sorting",
          body: "Tokens can now be searched and sorted directly from the frontend table.",
        },
        {
          date: "May 9, 2026",
          title: "Job detail refresh",
          body: "Job pages now have a cleaner layout, stronger post status visibility, and an improved upload modal.",
        },
      ],
      quickActions: [
        { label: "Create Job", icon: "plus-circle", to: "/new-automation" },
        { label: "Jobs", icon: "briefcase", to: "/jobs/all" },
        { label: "Tokens", icon: "key", to: "/tokens" },
        { label: "Bulk Delete", icon: "trash", to: "/bulk-delete/list" },
      ],
    };
  },
  computed: {
    user() {
      return this.$store.state.user || {};
    },
    userName() {
      return this.user.name || "there";
    },
    permissions() {
      return this.user.permissions || {};
    },
    currentDate() {
      return new Date().toLocaleDateString(undefined, {
        weekday: "long",
        month: "short",
        day: "numeric",
      });
    },
    metrics() {
      const data = this.dashboardData;
      return [
        {
          key: "jobs",
          label: "Total Jobs",
          value: data.jobs.total || 0,
          note: `${data.jobs.running || 0} running now`,
          icon: "briefcase",
          tone: "tone-blue",
        },
        {
          key: "success",
          label: "Published Posts",
          value: data.posts.successful || 0,
          note: `${data.posts.successRate || "0%"} success rate`,
          icon: "check-circle",
          tone: "tone-green",
        },
        {
          key: "upcoming",
          label: "Upcoming",
          value: data.upcomingPosts || 0,
          note: "Scheduled in next 24 hours",
          icon: "calendar2-week",
          tone: "tone-amber",
        },
        {
          key: "tokens",
          label: "Expiring Tokens",
          value: data.expiringTokens || 0,
          note: "Within the next 7 days",
          icon: "shield-check",
          tone: "tone-red",
        },
      ];
    },
    visibleRecentJobs() {
      return this.recentJobs.slice(0, 3);
    },
    visibleChangeLog() {
      return this.changeLog.slice(0, 2);
    },
  },
  mounted() {
    this.fetchDashboardData();
    if (this.permissions.allowServerStats) {
      this.fetchStorageData();
    }
  },
  methods: {
    async fetchDashboardData() {
      try {
        this.loadingDashboard = true;
        const response = await axios.get("/api/dashboard-data");
        this.dashboardData = response.data;
        this.recentJobs = response.data.recentJobs || [];
      } catch (error) {
        this.$toast.error("Failed to load dashboard data");
      } finally {
        this.loadingDashboard = false;
      }
    },
    async fetchStorageData() {
      try {
        this.loadingStorage = true;
        const response = await axios.post("/server-info");
        this.storageData = response.data;
      } catch (error) {
        this.$toast.error("Failed to load storage data");
      } finally {
        this.loadingStorage = false;
      }
    },
    statusClass(status) {
      return {
        "status-running": status === "Running",
        "status-completed": status === "Completed",
        "status-paused": status === "Paused",
        "status-error": status === "Error" || status === "Failed",
        "status-muted": !status,
      };
    },
  },
};
</script>

<style scoped>
.dashboard-home {
  background: #f7f9fc;
  border-radius: 12px;
  padding: 1rem;
  color: #162033;
  max-width: 100%;
  overflow-x: hidden;
  overflow-y: visible;
  box-sizing: border-box;
}

.dashboard-home *,
.dashboard-home *::before,
.dashboard-home *::after {
  box-sizing: border-box;
}

.hero-panel {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  background: #ffffff;
  border: 1px solid #e5e9f0;
  border-radius: 12px;
  padding: 1.15rem;
  box-shadow: 0 10px 28px rgba(16, 24, 40, 0.05);
  min-width: 0;
}

.hero-copy {
  max-width: 720px;
}

.eyebrow {
  display: inline-flex;
  color: #496179;
  font-size: 0.85rem;
  font-weight: 700;
  margin-bottom: 0.45rem;
}

.hero-copy h1 {
  font-size: 1.75rem;
  font-weight: 800;
  margin: 0;
  letter-spacing: 0;
}

.hero-copy p {
  color: #5d6f83;
  margin: 0.45rem 0 0;
  font-size: 0.95rem;
  max-width: 620px;
}

.hero-pulse {
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
  margin-top: 0.8rem;
}

.hero-pulse span {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.6rem;
  border: 1px solid #e5e9f0;
  border-radius: 999px;
  color: #496179;
  background: #fbfcfe;
  font-size: 0.82rem;
  font-weight: 700;
  white-space: nowrap;
}

.hero-side {
  display: grid;
  grid-template-columns: 168px;
  gap: 0.75rem;
  justify-items: stretch;
}

.automation-signal {
  position: relative;
  min-height: 112px;
  padding: 0.7rem;
  border: 1px solid #dde7f2;
  border-radius: 12px;
  background:
    linear-gradient(90deg, rgba(11, 102, 216, 0.08) 1px, transparent 1px),
    linear-gradient(0deg, rgba(11, 102, 216, 0.08) 1px, transparent 1px),
    #fbfdff;
  background-size: 18px 18px;
  overflow: hidden;
}

.automation-signal::after {
  content: "";
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(
    90deg,
    transparent,
    rgba(11, 102, 216, 0.16),
    transparent
  );
  animation: signalSweep 3.2s linear infinite;
}

.signal-header,
.signal-footer {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #52667d;
  font-size: 0.68rem;
  font-weight: 800;
}

.signal-header i {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: #24b36b;
  box-shadow: 0 0 0 0 rgba(36, 179, 107, 0.4);
  animation: statusPing 1.8s ease-out infinite;
}

.signal-bars {
  position: relative;
  z-index: 1;
  height: 56px;
  display: flex;
  align-items: end;
  gap: 4px;
  margin: 0.45rem 0;
}

.signal-bars span {
  flex: 1;
  min-width: 0;
  border-radius: 999px 999px 3px 3px;
  background: #0b66d8;
  opacity: 0.2;
  height: 32%;
  animation: signalBars 1.4s ease-in-out infinite;
}

.signal-bars span:nth-child(2n) {
  animation-delay: 0.16s;
}

.signal-bars span:nth-child(3n) {
  animation-delay: 0.32s;
}

.signal-bars span:nth-child(5n) {
  animation-delay: 0.48s;
}

.hero-actions {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.55rem;
}

.hero-action {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.55rem;
  min-height: 42px;
  padding: 0.45rem 0.7rem;
  border-radius: 10px;
  border: 1px solid #dde7f2 !important;
  font-weight: 800;
  box-shadow: none !important;
  transition: transform 0.16s ease, border-color 0.16s ease,
    background 0.16s ease;
}

.hero-action:hover {
  transform: translateY(-1px);
}

.hero-action-primary {
  background: #0b66d8 !important;
  color: #fff !important;
  border-color: #0b66d8 !important;
}

.hero-action-secondary {
  background: #ffffff !important;
  color: #162033 !important;
}

.hero-action-secondary:hover {
  border-color: #9eb8d4 !important;
  color: #0b66d8 !important;
}

.hero-action-icon {
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.18);
  flex: 0 0 auto;
}

.hero-action-secondary .hero-action-icon {
  background: #edf5ff;
  color: #0b66d8;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
  margin-top: 0.75rem;
}

.metric-card,
.panel {
  background: #ffffff;
  border: 1px solid #e5e9f0;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(16, 24, 40, 0.04);
}

.metric-card {
  padding: 0.85rem;
  min-width: 0;
}

.metric-topline {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  color: #5d6f83;
  font-size: 0.9rem;
  font-weight: 700;
}

.metric-icon {
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
}

.metric-card strong {
  display: block;
  font-size: 1.55rem;
  line-height: 1;
  margin-top: 0.75rem;
}

.metric-card small {
  display: block;
  color: #718297;
  margin-top: 0.35rem;
}

.tone-blue {
  background: #eaf3ff;
  color: #0b66d8;
}

.tone-green {
  background: #eaf8ef;
  color: #178447;
}

.tone-amber {
  background: #fff6dd;
  color: #a86400;
}

.tone-red {
  background: #fff0f0;
  color: #c93636;
}

.dashboard-grid,
.lower-grid {
  display: grid;
  gap: 0.75rem;
  margin-top: 0.75rem;
}

.dashboard-grid {
  grid-template-columns: minmax(0, 1.35fr) minmax(300px, 0.75fr);
}

.lower-grid {
  grid-template-columns: minmax(0, 0.95fr) minmax(300px, 0.55fr);
}

.panel {
  padding: 0.9rem;
  min-width: 0;
  overflow-x: hidden;
  overflow-y: visible;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  margin-bottom: 0.75rem;
  min-width: 0;
}

.panel-header h2 {
  font-size: 1.05rem;
  font-weight: 800;
  margin: 0;
}

.panel-header p {
  color: #718297;
  margin: 0.2rem 0 0;
  font-size: 0.9rem;
}

.job-stream {
  display: flex;
  flex-direction: column;
  border: 1px solid #eef2f6;
  border-radius: 10px;
  overflow: hidden;
}

.job-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  gap: 0.75rem;
  align-items: center;
  padding: 0.72rem;
  color: #162033;
  text-decoration: none;
  border-bottom: 1px solid #eef2f6;
  transition: background 0.15s ease;
}

.job-row:last-child {
  border-bottom: none;
}

.job-row:hover {
  background: #f8fafc;
  text-decoration: none;
}

.job-main {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.job-name {
  font-weight: 750;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.job-meta,
.job-type {
  color: #718297;
  font-size: 0.86rem;
}

.status-pill {
  display: inline-flex;
  padding: 0.25rem 0.55rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  border: 1px solid #dde5ee;
  color: #536579;
  background: #f8fafc;
}

.status-running {
  background: #eaf3ff;
  border-color: #cfe4ff;
  color: #0b66d8;
}

.status-completed {
  background: #eaf8ef;
  border-color: #c8efd7;
  color: #178447;
}

.status-paused {
  background: #fff6dd;
  border-color: #f6df9a;
  color: #936000;
}

.status-error {
  background: #fff0f0;
  border-color: #ffd1d1;
  color: #c93636;
}

.change-list {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.change-item {
  display: grid;
  grid-template-columns: 92px minmax(0, 1fr);
  gap: 0.85rem;
  padding-bottom: 0.65rem;
  border-bottom: 1px solid #eef2f6;
}

.change-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.change-date {
  color: #496179;
  font-size: 0.78rem;
  font-weight: 800;
}

.change-item h3 {
  font-size: 0.95rem;
  font-weight: 800;
  margin: 0;
}

.change-item p {
  color: #718297;
  margin: 0.25rem 0 0;
  font-size: 0.9rem;
}

.action-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.6rem;
}

.quick-strip {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
  margin-top: 0.75rem;
}

.quick-action {
  border: 1px solid #e5e9f0;
  border-radius: 12px;
  padding: 0.64rem;
  color: #162033;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-weight: 750;
  background: #ffffff;
  min-width: 0;
  transition: border-color 0.15s ease, transform 0.15s ease,
    box-shadow 0.15s ease;
}

.quick-action:hover {
  border-color: #b8c7d8;
  color: #0b66d8;
  text-decoration: none;
  transform: translateY(-1px);
  box-shadow: 0 8px 20px rgba(16, 24, 40, 0.06);
}

.quick-action-icon {
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  color: #0b66d8;
  background: #edf5ff;
  flex: 0 0 auto;
}

.quick-action-text {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.quick-action-arrow {
  color: #8aa0b8;
  font-size: 1.25rem;
  flex: 0 0 auto;
}

.storage-body {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.storage-percent {
  color: #496179;
  font-weight: 800;
}

.storage-ring {
  --used: 0%;
  width: 108px;
  height: 108px;
  border-radius: 50%;
  background: conic-gradient(#0b66d8 var(--used), #e8eef5 0);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  position: relative;
  flex: 0 0 auto;
}

.storage-ring::after {
  content: "";
  position: absolute;
  inset: 12px;
  background: #fff;
  border-radius: 50%;
}

.storage-ring span,
.storage-ring small {
  position: relative;
  z-index: 1;
}

.storage-ring span {
  font-weight: 850;
  font-size: 1.05rem;
}

.storage-ring small {
  color: #718297;
}

.storage-facts {
  display: grid;
  gap: 0.55rem;
  flex: 1;
  min-width: 0;
}

.storage-facts div {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.6rem;
  border: 1px solid #eef2f6;
  border-radius: 10px;
}

.storage-facts span {
  color: #718297;
}

.empty-state {
  color: #718297;
  background: #f8fafc;
  border: 1px dashed #dce4ec;
  border-radius: 10px;
  padding: 1rem;
}

@media (max-width: 1100px) {
  .metric-grid,
  .action-grid,
  .quick-strip {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .dashboard-grid,
  .lower-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 767.98px) {
  .dashboard-home {
    padding: 0.75rem;
    overflow-x: hidden;
    overflow-y: visible;
  }

  .hero-panel,
  .panel-header,
  .storage-body {
    flex-direction: column;
  }

  .hero-side {
    width: 100%;
    grid-template-columns: 1fr;
  }

  .hero-actions {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .metric-grid,
  .action-grid,
  .quick-strip {
    grid-template-columns: 1fr;
  }

  .job-row {
    grid-template-columns: 1fr;
  }

  .change-item {
    grid-template-columns: 1fr;
  }
}

@keyframes signalSweep {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

@keyframes signalBars {
  0%,
  100% {
    height: 28%;
    opacity: 0.18;
  }
  50% {
    height: 92%;
    opacity: 0.62;
  }
}

@keyframes statusPing {
  0% {
    box-shadow: 0 0 0 0 rgba(36, 179, 107, 0.4);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(36, 179, 107, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(36, 179, 107, 0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .automation-signal::after,
  .signal-bars span,
  .signal-header i {
    animation: none;
  }
}
</style>
